/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { onClickNavUrl } from '@coursera-web/cds-labs';
import type { SerializedStyles } from '@emotion/serialize';

import useRouter from 'js/lib/useRouter';

import { Button } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import type { TrackingProps } from 'bundles/page/components/TrackedLink2';

type Props = ButtonProps &
  TrackingProps & {
    module: BlockLevelModule;
    position: number;
    styles?: SerializedStyles | ((theme: Theme) => SerializedStyles);
  };

const isValidUrl = function (urlString: string) {
  const regexp = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/;
  return regexp.test(urlString);
};

const ContentfulButton: React.FC<Props> = ({ module, children, styles, target, ...buttonProps }) => {
  const trackingName = eventingTransform(`${module.__typename}_${module.slug}_button_${buttonProps.position}`);
  const router = useRouter();
  const track = useTracker();
  if (!isValidUrl(buttonProps.href)) {
    return null;
  }

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    track('click_button', {
      button: {
        name: 'ent_website_contentful_button',
        linkName: trackingName,
        linkURL: buttonProps.href || '',
      },
    });
    if (!target) {
      onClickNavUrl(router, buttonProps.href)(e);
    }
  };
  return (
    <Button
      className="ContentfulButton"
      component={TrackedLink2}
      trackingName={trackingName}
      onClick={handleClick}
      target={target}
      css={styles}
      {...buttonProps}
    >
      {children}
    </Button>
  );
};

export default ContentfulButton;
